import React, { Component } from 'react';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import classNames from 'classnames';
import { formatDateShort } from 'utils/date/formatDate';
import numberFormat from 'utils/number/format';
import CustomTabs from 'components/CustomTabs';
import { toProperCase } from 'utils/string';
import { phoneFormatter } from 'utils/formatter';
import SVG from 'components/base/SVG';
import RemovePropertyConfirmModal from './RemovePropertyConfirmModal';
import { deleteSavedProperty, selectLoading } from 'data/property';
import { selectListManagementEnabled } from 'data/user';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

import ContactNameCellRenderer from './contactNameCellRenderer';
import CampaignNameCellRenderer from './campaignNameCellRenderer';
import GroupNameCellRenderer from './groupNameCellRenderer';
import CampaignContentCellRenderer from './campaignContentCellRenderer';
import SavedPropertyActionCellRenderer from './savedPropertyActionCellRenderer';
import ContactCellRenderer from '../../components/SearchTable/ContactCellRenderer';
import EmailCellRenderer from '../../components/SearchTable/EmailCellRenderer';
import NameCellRenderer from '../../components/SearchTable/NameCellRenderer';
import cssContactGrid from '../../components/SearchTable/style.scss'; //

import css from './style.scss';

const FieldMap = {
  listQuantity: { field: 'lists' },
  contactAppendQuantity: { field: 'appendedContacts' },
  campaignQuantity: { field: 'campaignItems' },
  voicemailCampaignQuantity: { field: 'campaignItems', type: 'VOICEMAIL' },
  postcardCampaignQuantity: { field: 'campaignItems', type: 'POSTCARD' },
  emailCampaignQuantity: { field: 'campaignItems', type: 'EMAIL' },
  dialerContactedCount: { field: 'campaignItems', type: 'DIALER' },
};

// Section headings for the Marketing Campaign Activities tab. Types not listed
// here fall back to proper-casing the raw value.
const CampaignTypeLabels = {
  DIALER: 'Dialer',
  POSTCARD: 'Direct Mail',
  EMAIL: 'Email',
  VOICEMAIL: 'Voicemail',
};

// Fixed display order; anything unrecognized is appended in the order it appears.
const CampaignTypeOrder = ['DIALER', 'POSTCARD', 'EMAIL', 'VOICEMAIL'];

/** Splits campaign activities into one ordered section per campaign type. */
function buildSections(rowData, groupField) {
  const byType = new Map();

  (rowData || []).forEach((row) => {
    const key = row[groupField] || '';
    if (!byType.has(key)) byType.set(key, []);
    byType.get(key).push(row);
  });

  return [...byType.keys()]
    .sort((a, b) => {
      const indexA = CampaignTypeOrder.indexOf(a);
      const indexB = CampaignTypeOrder.indexOf(b);
      return (indexA < 0 ? Number.MAX_SAFE_INTEGER : indexA) - (indexB < 0 ? Number.MAX_SAFE_INTEGER : indexB);
    })
    .map(key => ({
      key,
      label: CampaignTypeLabels[key] || toProperCase(key),
      rowData: byType.get(key),
    }));
}

class DetailCellRenderer extends Component {
  constructor(props) {
    super(props);

    this.handleGridReady = this.handleGridReady.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);
    this.handleSectionToggle = this.handleSectionToggle.bind(this);
    this.handleDeleteProperty = this.handleDeleteProperty.bind(this);
    this.handleRemovePropertyConfirm = this.handleRemovePropertyConfirm.bind(this);
    this.handleRemovePropertyCancel = this.handleRemovePropertyCancel.bind(this);

    const { context, data: { id } } = props;
    const { openPostcardPreview, detailExpandField, setActiveDetail, groupId } = context;

    setActiveDetail(this);

    this.expandField = FieldMap[detailExpandField] || FieldMap['listQuantity'];

    const dateFormatter = d => formatDateShort(d.value);
    const numberFormatter = d => numberFormat(d.value);
    const properCaseFormatter = d => toProperCase(d.value);
    const phoneCellFormatter = d => phoneFormatter(d.value);

    const gridProps = {
      onGridReady: this.handleGridReady,
      onFirstDataRendered: ({ api }) => { api.sizeColumnsToFit(); },
      getRowId: params => params.data.id,
      popupParent: document.body,
      suppressCellFocus: true,
      enableCellTextSelection: true,
      groupDefaultExpanded: 1,
      groupDisplayType: 'groupRows',
      defaultColDef: { resizable: true, filter: false, sortable: true, width: 80 },
      context: {
        id,
        groupId,
        openPostcardPreview,
        handleDeleteProperty: this.handleDeleteProperty,
      },
      components: {
        contactNameCellRenderer: ContactNameCellRenderer,
        campaignNameCellRenderer: CampaignNameCellRenderer,
        groupNameCellRenderer: GroupNameCellRenderer,
        campaignContentCellRenderer: CampaignContentCellRenderer,
        savedPropertyActionCellRenderer: SavedPropertyActionCellRenderer,
        nameCellRenderer: NameCellRenderer,
        contactCellRenderer: ContactCellRenderer,
        emailCellRenderer: EmailCellRenderer
      },
    };

    this.tabs = {
      lists: {
        title: 'Marketing Lists',
        gridProps: {
          ...gridProps,
          domLayout: 'autoHeight',
          columnDefs: [
            { headerName: 'Address', field: 'name', width: 80, minWidth: 80, cellRenderer: 'groupNameCellRenderer', suppressColumnsToolPanel: true },
            { headerName: 'Date Added', field: 'createDate', width: 80, minWidth: 80, valueFormatter: dateFormatter },
            { headerName: '', field: 'savedPropertyId', width: 80, minWidth: 50, cellRenderer: 'savedPropertyActionCellRenderer', cellClass: css.actions, suppressMovable: true, filter: false, suppressMenu: true, sortable: false, resizable: false },
          ],
        },
      },

      campaignItems: {
        title: 'Marketing Campaign Activities',
        // Rendered as one collapsible section (with its own grid) per campaign
        // type instead of ag-grid row grouping — see `renderSections`.
        groupField: 'type',
        gridProps: {
          ...gridProps,
          domLayout: 'autoHeight',
          columnDefs: [
            { headerName: 'Campaign Activity Name', field: 'name', cellRenderer: 'campaignNameCellRenderer', suppressColumnsToolPanel: true },
            { headerName: 'Status', field: 'status', valueFormatter: properCaseFormatter },
            { headerName: 'Date', field: 'publishDate', width: 80, valueFormatter: dateFormatter },
            { headerName: 'Recipients', field: 'recipientTotal', valueFormatter: numberFormatter },
            { headerName: 'Delivered', field: 'deliveredQuantity', valueFormatter: numberFormatter },
            { headerName: 'Content', field: 'previewUrl', cellRenderer: 'campaignContentCellRenderer', autoHeight: true, width: 160 },
          ],
        },
      },

      appendedContacts: {
        title: 'Skip Trace History',
        gridProps: {
          ...gridProps,
          getRowId: params => `${params.data.id}+${params.data.processedDate}`,
          domLayout: 'autoHeight',
          // Each phone/email renders on its own line (18px line-height + 4px gap),
          // so the row has to grow with the tallest column or the lines overlap
          // the neighbouring rows.
          getRowHeight: ({ data }) => {
            const lineHeight = 22;
            const cellPadding = 14;
            const maxLines = Math.max(
              1,
              data.mobilePhone ? data.mobilePhone.length : 0,
              data.landlinePhone ? data.landlinePhone.length : 0,
              data.otherPhone ? data.otherPhone.length : 0,
              data.emails ? data.emails.length : 0,
            );
            return cellPadding + (maxLines * lineHeight);
          },
          defaultColDef: {
            ...gridProps.defaultColDef,
            // Balham pins .ag-cell line-height to the theme row height, which
            // vertically centres single lines and lets stacked ones spill out.
            cellClass: css.multiLineCell,
          },
          columnDefs: [
            {
              headerName: 'Name',
              field: 'name',
              width: 80,
              minWidth:60,
              cellRenderer: "nameCellRenderer",
              suppressColumnsToolPanel: true,
            },
            { headerName: 'Contact Type', field: 'contactType', width: 100 , minWidth:80, },
            { headerName: 'Date', field: 'processedDate', width: 80, minWidth:60 , valueFormatter: dateFormatter },
            {
              headerName: 'Mobile',
              field: 'mobilePhone',
              minWidth: 140,
              cellRenderer: "contactCellRenderer",
              comparator: (numbersA, numbersB, nodeA, nodeB, isDescending) => {
                if (!numbersA && !numbersB) return 0;
                if (!numbersA) return -1;
                if (!numbersB) return 1;
                const valueA = numbersA.map(number =>  number.number).join(" ");
                const valueB = numbersB.map(number =>  number.number).join(" ");
                if(valueA == valueB ) return 0
                return (valueA > valueB) ? 1 : -1;
              }
            },
            {
              headerName: 'Landline',
              field: 'landlinePhone',
              minWidth: 140,
              cellRenderer: "contactCellRenderer",
              comparator: (numbersA, numbersB, nodeA, nodeB, isDescending) => {
                if (!numbersA && !numbersB) return 0;
                if (!numbersA) return -1;
                if (!numbersB) return 1;
                const valueA = numbersA.map(number =>  number.number).join(" ");
                const valueB = numbersB.map(number =>  number.number).join(" ");
                if(valueA == valueB ) return 0
                return (valueA > valueB) ? 1 : -1;
              }
            },
            {
              headerName: 'Other',
              field: 'otherPhone',
              minWidth: 140,
              cellRenderer: "contactCellRenderer",
              comparator: (numbersA, numbersB, nodeA, nodeB, isDescending) => {
                if (!numbersA && !numbersB) return 0;
                if (!numbersA) return -1;
                if (!numbersB) return 1;
                const valueA = numbersA.map(number =>  number.number).join(" ");
                const valueB = numbersB.map(number =>  number.number).join(" ");
                if(valueA == valueB ) return 0
                return (valueA > valueB) ? 1 : -1;
              }
            },
            {
              headerName: 'Email',
              field: 'emails',
              minWidth: 150,
              cellRenderer: "emailCellRenderer",
              comparator: (emailsA, emailsB, nodeA, nodeB, isDescending) => {
                if (!emailsA && !emailsB) return 0;
                if (!emailsA) return -1;
                if (!emailsB) return 1;
                const valueA = emailsA.join(" ");
                const valueB = emailsB.join(" ");
                if(valueA === valueB) return 0;
                return (valueA > valueB) ? 1 : -1;
              }
            },
          ],
        },
      },
    };

    this.state = { loaded: false, tabs: [], tabIndex: 0, expandedSections: [], removePropertyModal: null };
  }

  componentWillMount() {
    const { data: { id }, context: { getSavedPropertyDetail }, lmEnabled } = this.props;
    getSavedPropertyDetail(id, ({ response }) => {
      const tabs = (lmEnabled ? ['lists', 'campaignItems', 'appendedContacts'] : ['lists']).filter(field => (response[field] || []).length).map((field) => {
        const tab = this.tabs[field];
        tab.field = field;
        tab.gridProps.rowData = response[field];
        if (tab.groupField) tab.sections = buildSections(response[field], tab.groupField);
        return tab;
      });

      const expandField = this.expandField;
      const tabIndex = expandField
        ? tabs.findIndex(t => t.field === expandField.field)
        : -1;

      this.setState({
        loaded: true,
        tabs,
        tabIndex: tabIndex >= 0 ? tabIndex : 0,
        expandedSections: this.getInitialExpandedSections(tabs),
      });
    });
  }

  /**
   * Opens only the section the user drilled into (e.g. clicking the Direct Mail
   * count in the grid); with no specific type every section starts open, which
   * matches the previous `groupDefaultExpanded: 1` behavior.
   */
  getInitialExpandedSections(tabs) {
    const expandField = this.expandField;
    const sections = (tabs.find(t => !!t.groupField) || {}).sections || [];

    if (expandField && expandField.type) {
      // Consume the request so `loadType` doesn't try to re-apply it to a grid.
      this.expandField = null;
      const match = sections.find(s => s.key === expandField.type);
      if (match) return [match.key];
    }

    return sections.map(s => s.key);
  }

  loadField(loadField) {
    this.expandField = FieldMap[loadField] || FieldMap['listQuantity'];

    const { tabs } = this.state;
    const tabIndex = tabs.findIndex(t => t.field === this.expandField.field);
    const tab = tabs[tabIndex];

    // Sectioned tabs own their expand state in React, so resolve it here rather
    // than waiting for a grid to report ready.
    if (tab && tab.groupField) {
      this.setState({ tabIndex, expandedSections: this.getInitialExpandedSections(tabs) });
      return;
    }

    if (tabIndex !== this.state.tabIndex) this.setState({ tabIndex });
    else if (tab && tab.api) this.loadType();
  }

  handleSectionToggle(key) {
    this.setState(({ expandedSections }) => ({
      expandedSections: expandedSections.includes(key)
        ? expandedSections.filter(k => k !== key)
        : [...expandedSections, key],
    }));
  }

  handleGridReady(field, api) {
    this.tabs[field].api = api;
    this.loadType();
  }

  loadType() {
    if (this.expandField) {
      const { field, type } = this.expandField;
      this.expandField = null;

      const tab = this.tabs[field];
      if (tab && tab.api) {
        tab.api.forEachNode((node) => {
          if (!node.level) node.setExpanded(!type || node.key === type);
        });
      }
    }
  }

  handleTabSelect(tabIndex) {
    this.setState({ tabIndex });
  }

  handleDeleteProperty(deleteId) {
    const { deleteSavedProperty, context: { search }, data: { id }, loading, node } = this.props;
    if (loading) return;

    this.setState({
      removePropertyModal: {
        onConfirm: () => deleteSavedProperty(deleteId, id, undefined, ({ response: { savedProperty: { lists = [] } } }) => {
          this.tabs.lists.api.setRowData(lists);
          if (!lists.length) search(false);
          else node?.setDataValue?.('listQuantity', lists.length);
        }),
      },
    });
  }

  handleRemovePropertyConfirm() {
    const { removePropertyModal } = this.state;
    this.setState({ removePropertyModal: null });
    if (removePropertyModal?.onConfirm) removePropertyModal.onConfirm();
  }

  handleRemovePropertyCancel() {
    this.setState({ removePropertyModal: null });
  }

  renderSections({ field, gridProps, sections = [] }) {
    const { expandedSections } = this.state;

    return (
      <div className={css.sections}>
        {sections.map(({ key, label, rowData }) => {
          const expanded = expandedSections.includes(key);

          return (
            <div key={key} className={css.section}>
              <button
                type="button"
                className={css.sectionHeader}
                aria-expanded={expanded}
                onClick={() => this.handleSectionToggle(key)}
              >
                <svg className={classNames(css.sectionCaret, { [css.sectionCaretOpen]: expanded })} xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none">
                  <path d="M8.19503 6.80419C8.57822 7.18739 8.57822 7.80701 8.19503 8.18613L1.67263 14.7126C1.28943 15.0958 0.669806 15.0958 0.290691 14.7126C-0.0884237 14.3294 -0.0924997 13.7098 0.290691 13.3307L6.12009 7.50128L0.286614 1.6678C-0.0965767 1.28461 -0.0965767 0.664981 0.286614 0.285867C0.669806 -0.0932479 1.28943 -0.0973244 1.66855 0.285867L8.19503 6.80419Z" fill="#66707D"/>
                </svg>
                <span>{`${label} (${rowData.length})`}</span>
              </button>

              {expanded && (
                <div className={classNames("ag-theme-balham", cssContactGrid.grid, css.detailGrid, css.sectionGrid)}>
                  <AgGridReact
                    {...gridProps}
                    rowData={rowData}
                    onGridReady={({ api }) => this.handleGridReady(field, api)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const { loaded, tabs, tabIndex } = this.state;
    const { data: { id } } = this.props;
    return (
      <div className={css.detailContainer} data-property-id={id}>
        {!loaded ? (
          <div className={css.detailLoading}>
            <SVG icon="spinner" size={20} />
            <span>Loading...</span>
          </div>
        ) : (
          <div className={css.detail}>
            <div className={css.detailInner}>
              <div kind="default" className={css.detailTabs}>
                <Tabs selectedIndex={tabIndex} onSelect={this.handleTabSelect}>
                  <TabList>
                    {tabs.map(({ title }) => <Tab key={title}>{title}</Tab>)}
                  </TabList>
                  {tabs.map(tab => (
                    <TabPanel key={tab.field}>
                      {tab.groupField ? this.renderSections(tab) : (
                        <div className={classNames("ag-theme-balham", cssContactGrid.grid, css.detailGrid, css.sectionGrid)}>
                          <AgGridReact {...tab.gridProps} onGridReady={({ api }) => this.handleGridReady(tab.field, api)} />
                        </div>
                      )}
                    </TabPanel>
                  ))}
                </Tabs>
              </div>
            </div>
          </div>
        )}

        {this.state.removePropertyModal && (
          <RemovePropertyConfirmModal
            onConfirm={this.handleRemovePropertyConfirm}
            onCancel={this.handleRemovePropertyCancel}
          />
        )}
      </div>
    );
  }
}

export default connect(state => ({
  loading: selectLoading(state),
  lmEnabled: selectListManagementEnabled(state),
}), {
  deleteSavedProperty,
})(DetailCellRenderer);
